import { DatePipe } from '@angular/common';
import { Component, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Document } from '../../model';

/**
 * The customer-facing trust label for `Document.status`. Renders nothing for `PENDING` or a
 * missing status by design (app#242 / E3b) — the legacy corpus must stay neutral, not blemished.
 *
 * With `documentDate` set the badge additionally carries a tooltip naming the document's own date
 * (NOT the review date). Without it the binding stays an empty string, which is how Material is
 * told to suppress the tooltip entirely — a missing date must produce no hover target at all,
 * never an empty black box.
 */
@Component({
  selector: 'app-document-review-badge',
  standalone: true,
  imports: [DatePipe, MatIconModule, MatTooltipModule, TranslateModule],
  template: `
    @let tooltip =
      documentDate()
        ? ('document_review_badge_date_tooltip_tr' | translate: { date: documentDate() | date: 'mediumDate' })
        : '';
    @if (status() === 'APPROVED') {
      <span
        class="document-review-badge document-review-badge--approved"
        data-test="document-review-badge-approved"
        [matTooltip]="tooltip"
      >
        <mat-icon>verified</mat-icon>
        {{ 'document_reviewed_by_supplycanvas_tr' | translate }}
      </span>
    } @else if (status() === 'INVALID') {
      <span
        class="document-review-badge document-review-badge--invalid"
        data-test="document-review-badge-invalid"
        [matTooltip]="tooltip"
      >
        <mat-icon>report_problem</mat-icon>
        {{ 'document_rejected_by_supplycanvas_tr' | translate }}
      </span>
    }
  `,
  styles: [
    `
      .document-review-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.75em;
        line-height: 1;
        padding: 3px 8px;
        border-radius: 12px;
        white-space: nowrap;
      }
      .document-review-badge mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
      }
      .document-review-badge--approved {
        color: #2e7d32;
        background: rgba(46, 125, 50, 0.1);
      }
      .document-review-badge--invalid {
        color: #b45309;
        background: rgba(180, 83, 9, 0.1);
      }
    `,
  ],
})
export class DocumentReviewBadgeComponent {
  status = input<Document['status']>();
  documentDate = input<string | undefined>();
}
